import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { municipalities } from '../data/municipalities';
import { farmersAPI, processorsAPI } from '../services/api';
import { useAnimatedCounter } from '../hooks/useAnimatedCounter';
import {
  SimpleBarChart,
  SimpleLineChart,
  SimplePieChart,
  SimpleStackedBar,
  SimpleFunnelChart,
  SimpleBubbleChart,
} from '../components/charts/SimpleCharts';

export function Analytics() {
  // Live data state
  const [farmers, setFarmers] = useState([]);
  const [processors, setProcessors] = useState([]);
  const [stats, setStats] = useState(null);
  const [municipalityDataState, setMunicipalityDataState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingCapacityDataState, setProcessingCapacityDataState] = useState(null);
  const [processingCapacityLoading, setProcessingCapacityLoading] = useState(false);

  // Filter states
  const [selectedView, setSelectedView] = useState('all');

  // Helper function to extract municipality from address (same as ProcessorsDirectory)
  const extractMunicipality = useCallback((address) => {
    if (!address) return null;
    
    const normalizedAddress = address.toLowerCase().trim();
    
    // Search for any known municipality name in the address
    for (const municipality of municipalities) {
      const normalizedMunicipalityName = municipality.toLowerCase();
      if (normalizedAddress.includes(normalizedMunicipalityName)) {
        return municipality;
      }
    }
    
    // If no exact match found, try to extract from common patterns
    const parts = address.split(',').map(p => p.trim());
    
    // Check the last part (likely province or municipality)
    if (parts.length > 0) {
      const lastPart = parts[parts.length - 1];
      if (lastPart.toLowerCase() !== 'bohol') {
        const match = municipalities.find(m => 
          m.toLowerCase() === lastPart.toLowerCase()
        );
        if (match) return match;
      }
    }
    
    // Check second-to-last part if exists
    if (parts.length > 1) {
      const secondLast = parts[parts.length - 2];
      const match = municipalities.find(m => 
        m.toLowerCase() === secondLast.toLowerCase()
      );
      if (match) return match;
    }
    
    return null;
  }, []);

  // Map farmers data using the same robust mapping as FarmersDirectory
  const mapFarmersData = useCallback((dbArray = []) => {
    return (dbArray || []).map((f, index) => {
      if (!f || typeof f !== 'object') return null;
      
      const first = f.Firstname ?? f.firstname ?? f.first_name ?? f.firstName ?? f.first;
      const last = f.Lastname ?? f.lastname ?? f.last_name ?? f.lastName ?? f.last;
      const fullNameGuess = `${first || ''} ${last || ''}`.trim();
      const name = (f.name ?? fullNameGuess) || f.FullName || f.fullname || f.displayName;
      const municipality = f.Municipality ?? f.municipality ?? f.Municipal ?? '';
      const age = f.Age ?? f.age ?? f.AGE ?? 0;
      const gender = f.Gender ?? f.gender ?? f.sex ?? '';
      const farmSize = f.FarmArea ?? f.farmArea ?? f.farm_size ?? f.farmSize ?? f.Farm_Area_ha ?? 0;
      const landOwnership = f.Land_Ownership ?? f.LandOwnership ?? f.landOwnership ?? f.land_ownership ?? f.landOwnershipType ?? '';
      const farmingExperience = f.Years_Farming ?? f.farmingExperience ?? f.Experience ?? f.experience ?? f.farming_experience ?? 0;
      const production = f.Weight_of_beans_per_hectare_kg ?? f.weightOfBeansPerHectare ?? f.production ?? f.productionCapacity ?? 0;
      
      return {
        id: f.Farmer_ID ?? f.farmer_id ?? f.id ?? f.Id ?? f.ID ?? index + 1,
        name,
        municipality,
        age: parseInt(age) || 0,
        gender,
        landSize: parseFloat(farmSize) || 0,
        landOwnership,
        yearsExperience: parseInt(farmingExperience) || 0,
        productionCapacity: parseFloat(production) || 0
      };
    }).filter(Boolean);
  }, []);

  // Map processors data using the same robust mapping as ProcessorsDirectory
  const mapProcessorsData = useCallback((dbArray = []) => {
    return (dbArray || []).map((processor, index) => {
      if (!processor || typeof processor !== 'object') return null;
      
      const address = processor.Address || processor.address;
      const extractedMunicipality = extractMunicipality(address);
      const businessName = processor.Name_of_Business || processor.businessName || processor.business_name || processor.name || processor.displayName || 'Unknown';
      const businessType = (processor.Type_of_Business || processor.businessType || processor.typeBusiness || processor.type || '').trim() || 'Unknown';
      const municipality = processor.municipality || extractedMunicipality;
      const processingCapacity = parseFloat(processor['Quantity_of_Cacao_Beans_per_Processing_Cycle_kg'] || processor.processingCapacity || processor.processing_capacity || 0);
      const yearsInOperation = parseInt(processor.Length_of_Business_Operation_in_Years || processor.yearsInOperation || processor.years_in_operation || 0);
      
      // Calculate marketReach dynamically
      const hasLocal = processor.Local_Market && processor.Local_Market !== 'No';
      const hasInternational = processor.International && processor.International !== 'No';
      let marketReach = 'Not specified';
      if (hasLocal && hasInternational) {
        marketReach = 'Local & International';
      } else if (hasInternational) {
        marketReach = 'International';
      } else if (hasLocal) {
        marketReach = 'Local';
      } else if (processor.Market_Reach || processor.marketReach || processor.market_reach) {
        marketReach = processor.Market_Reach || processor.marketReach || processor.market_reach;
      }
      
      return {
        id: processor.Business_ID || processor.id || index + 1,
        businessName,
        businessType,
        municipality,
        marketReach,
        processingCapacity,
        yearsInOperation
      };
    }).filter(Boolean);
  }, [extractMunicipality]);

  // Fetch analytics data from backend with real-time updates
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      try {
        // Fetch farmers and processors data directly from API
        const [farmersResponse, processorsResponse] = await Promise.all([
          farmersAPI.getAll(),
          processorsAPI.getAll()
        ]);

        if (!mounted) return;

        // Extract data from responses
        const farmersData = farmersResponse?.data ?? farmersResponse?.rows ?? farmersResponse ?? [];
        const processorsData = processorsResponse?.data ?? processorsResponse?.rows ?? processorsResponse ?? [];

        // Map data using the same robust mapping as the directories
        const normalizedFarmers = mapFarmersData(farmersData);
        const normalizedProcessors = mapProcessorsData(processorsData);

        setFarmers(normalizedFarmers);
        setProcessors(normalizedProcessors);

        // Calculate aggregate stats
        const totalHectares = normalizedFarmers.reduce((sum, f) => sum + f.landSize, 0);
        const totalProductionKg = normalizedFarmers.reduce((sum, f) => sum + f.productionCapacity, 0);

        setStats({
          registeredFarmers: normalizedFarmers.length,
          activeProcessors: normalizedProcessors.length,
          totalHectares: Math.round(totalHectares),
          totalProductionKg: Math.round(totalProductionKg)
        });

        // Calculate municipality data with proper filtering
        const municipalityMap = new Map();
        
        normalizedFarmers.forEach(farmer => {
          const mun = farmer.municipality;
          // Only count farmers with valid municipalities
          if (mun && mun !== 'Unknown' && mun.trim() !== '') {
            if (!municipalityMap.has(mun)) {
              municipalityMap.set(mun, { municipality: mun, farmers: 0, processors: 0 });
            }
            municipalityMap.get(mun).farmers++;
          }
        });

        normalizedProcessors.forEach(processor => {
          const mun = processor.municipality;
          // Only count processors with valid municipalities
          if (mun && mun !== 'Unknown' && mun.trim() !== '') {
            if (!municipalityMap.has(mun)) {
              municipalityMap.set(mun, { municipality: mun, farmers: 0, processors: 0 });
            }
            municipalityMap.get(mun).processors++;
          }
        });

        const municipalityDataArray = Array.from(municipalityMap.values())
          .filter(item => (item.farmers > 0 || item.processors > 0) && item.municipality && item.municipality !== 'Unknown')
          .sort((a, b) => (b.farmers + b.processors) - (a.farmers + a.processors))
          .slice(0, 10)
          .map(item => ({
            ...item,
            municipality: item.municipality.length > 12 ? item.municipality.substring(0, 12) + '...' : item.municipality
          }));

        setMunicipalityDataState(municipalityDataArray);

        // Also prepare processing capacity cache for quicker render
        const procCapacity = normalizedProcessors.map((processor, index) => {
          // Use the same robust logic as ProcessorsDirectory
          let name = processor.businessName || processor.nameOfBusiness || processor.name || processor.displayName || `P${index + 1}`;
          if (name.length > 15) name = name.substring(0, 15) + '...';
          return {
            name,
            capacity: processor.processingCapacity || 0,
            years: processor.yearsInOperation || 0
          };
        }).sort((a, b) => b.capacity - a.capacity).slice(0, 10);

        setProcessingCapacityDataState(procCapacity);

        // Prepare municipality grouped capacities so stacked view is ready
        try {
          const byMun = {};
          normalizedProcessors.forEach(proc => {
            const mun = proc.municipality || 'Unknown';
            const reach = proc.marketReach || 'Local';
            if (!byMun[mun]) byMun[mun] = { municipality: mun, total: 0, segments: {} };
            byMun[mun].segments[reach] = (byMun[mun].segments[reach] || 0) + (proc.processingCapacity || 0);
            byMun[mun].total += (proc.processingCapacity || 0);
          });

          // Municipality data prepared (sorted and limited top 10)
          Object.values(byMun).map(m => ({
            municipality: m.municipality,
            total: m.total,
            segments: Object.entries(m.segments).map(([name, value]) => ({ name, value }))
          })).sort((a, b) => b.total - a.total).slice(0, 10);

        } catch (e) {
          // ignore grouping errors
        }

      } catch (err) {
        setError(err?.message || String(err));
        // Set empty arrays on error
        setFarmers([]);
        setProcessors([]);
        setStats({
          registeredFarmers: 0,
          activeProcessors: 0,
          totalHectares: 0,
          totalProductionKg: 0
        });
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    load();
    
    return () => { 
      mounted = false; 
    };
  }, [mapFarmersData, mapProcessorsData]);

  // Animated counters for stats
  const animatedFarmersCount = useAnimatedCounter(stats?.registeredFarmers || 0);
  const animatedProcessorsCount = useAnimatedCounter(stats?.activeProcessors || 0);
  const animatedHectares = useAnimatedCounter(stats?.totalHectares || 0);

  // Process data for analytics (must come before filtered data)
  const effectiveFarmers = farmers || [];
  const effectiveProcessors = processors || [];

  const filteredFarmers = effectiveFarmers;
  const filteredProcessors = effectiveProcessors;

  const genderData = [
    { name: 'Male', value: filteredFarmers.filter(f => f.gender && f.gender.trim().toLowerCase() === 'male').length, color: '#92400e', percentage: 0 },
    { name: 'Female', value: filteredFarmers.filter(f => f.gender && f.gender.trim().toLowerCase() === 'female').length, color: '#059669', percentage: 0 }
  ];
  
  // Calculate percentages
  const totalFarmers = filteredFarmers.length || (stats?.registeredFarmers ?? 0);
  genderData.forEach(item => {
    item.percentage = totalFarmers ? ((item.value / totalFarmers) * 100).toFixed(1) : '0.0';
  });

  // Handle combined ownership types (e.g., "Owned/Inherited") by counting farmers in multiple categories
  const landOwnershipData = [
    { name: 'Owned', count: filteredFarmers.filter(f => (f.landOwnership || '').includes('Owned')).length, color: '#92400e' },
    { name: 'Tenanted', count: filteredFarmers.filter(f => (f.landOwnership || '').includes('Tenanted')).length, color: '#d97706' },
    { name: 'Inherited', count: filteredFarmers.filter(f => (f.landOwnership || '').includes('Inherited')).length, color: '#059669' },
    { name: 'Bought', count: filteredFarmers.filter(f => (f.landOwnership || '').includes('Bought')).length, color: '#0369a1' }
  ];

  // Convert age group information to a structure suitable for SimplePieChart
  const ageColors = ['#92400e', '#d97706', '#059669', '#0369a1'];
  const ageGroupData = [
    { name: '20-30', value: filteredFarmers.filter(f => f.age >= 20 && f.age < 30).length, color: ageColors[0] },
    { name: '30-40', value: filteredFarmers.filter(f => f.age >= 30 && f.age < 40).length, color: ageColors[1] },
    { name: '40-50', value: filteredFarmers.filter(f => f.age >= 40 && f.age < 50).length, color: ageColors[2] },
    { name: '50+', value: filteredFarmers.filter(f => f.age >= 50).length, color: ageColors[3] }
  ];

  const municipalityData = (municipalityDataState && Array.isArray(municipalityDataState) ? municipalityDataState :
    municipalities.slice(0, 8).map(municipality => ({
      municipality: municipality.length > 12 ? municipality.substring(0, 12) + '...' : municipality,
      farmers: filteredFarmers.filter(f => f.municipality === municipality).length,
      processors: filteredProcessors.filter(p => p.municipality === municipality).length
    })).filter(item => item.farmers > 0 || item.processors > 0)
  );

  const businessTypeData = [
    { name: 'Sole Proprietorship', value: filteredProcessors.filter(p => p.businessType && p.businessType.includes('Sole Proprietorship')).length, color: '#92400e' },
    { name: 'Corporation', value: filteredProcessors.filter(p => p.businessType && p.businessType.includes('Corporation')).length, color: '#d97706' },
    { name: 'Association', value: filteredProcessors.filter(p => p.businessType && p.businessType.includes('Association')).length, color: '#059669' }
  ];

  const marketReachData = [
    { 
      reach: 'Local', 
      count: filteredProcessors.filter(p => 
        p.marketReach === 'Local' || 
        p.marketReach === 'Local & International'
      ).length 
    },
    { 
      reach: 'International', 
      count: filteredProcessors.filter(p => 
        p.marketReach === 'International' || 
        p.marketReach === 'Local & International'
      ).length 
    },
    { 
      reach: 'Local & International', 
      count: filteredProcessors.filter(p => p.marketReach === 'Local & International').length 
    },
    { reach: 'Not specified', count: filteredProcessors.filter(p => p.marketReach === 'Not specified').length }
  ];

  const productionData = filteredFarmers.map((farmer, index) => {
    // Show actual farmer name, truncate if too long
    const farmerName = farmer.name || `Farmer ${index + 1}`;
    const displayName = farmerName.length > 20 ? farmerName.substring(0, 20) + '...' : farmerName;
    
    return {
      name: displayName,
      production: farmer.productionCapacity || 0,
      landSize: farmer.landSize || 0,
      experience: farmer.yearsExperience || 0
    };
  }).sort((a, b) => b.production - a.production).slice(0, 10);

  const processingCapacityData = filteredProcessors.map((processor, index) => ({
    name: (processor.businessName || `P${index + 1}`).length > 15 ? (processor.businessName || `P${index + 1}`).substring(0, 15) + '...' : (processor.businessName || `P${index + 1}`),
    capacity: processor.processingCapacity || 0,
    years: processor.yearsInOperation || 0
  })).sort((a, b) => b.capacity - a.capacity).slice(0, 10);

  // Fetcher for processor capacity using existing processorsAPI
  const fetchProcessingCapacity = async (opts = {}) => {
    setProcessingCapacityLoading(true);
    try {
      const resp = await processorsAPI.getAll(opts);
      const processorsData = resp?.data ?? resp?.rows ?? resp ?? [];
      const normalized = Array.isArray(processorsData) ? processorsData.map((processor, index) => ({
        name: (processor.Business_Name || processor.businessName || processor.business_name || `P${index + 1}`).length > 15 ? (processor.Business_Name || processor.businessName || processor.business_name || `P${index + 1}`).substring(0, 15) + '...' : (processor.Business_Name || processor.businessName || processor.business_name || `P${index + 1}`),
        capacity: parseFloat(processor.Processing_Capacity || processor.processingCapacity || processor.processing_capacity || 0),
        years: parseInt(processor.Years_in_Operation || processor.yearsInOperation || processor.years_in_operation || 0)
      })) : [];

      const sorted = normalized.sort((a, b) => b.capacity - a.capacity);
      setProcessingCapacityDataState(sorted);
      // Also prepare municipality grouped view for stacked bars
      try {
        const byMun = {};
        (sorted || []).forEach(p => {
          const mun = (p.municipality) || (p.municipalityName) || 'Unknown';
          const reach = (p.marketReach) || 'Local';
          if (!byMun[mun]) byMun[mun] = { municipality: mun, total: 0, segments: {} };
          byMun[mun].segments[reach] = (byMun[mun].segments[reach] || 0) + (p.capacity || 0);
          byMun[mun].total += (p.capacity || 0);
        });

        // Sort and limit top municipalities
        Object.values(byMun).map(m => ({
          municipality: m.municipality,
          total: m.total,
          segments: Object.entries(m.segments).map(([name, value]) => ({ name, value }))
        })).sort((a, b) => b.total - a.total).slice(0, 10);

      } catch (e) {
        // ignore grouping errors
      }
      return sorted;
    } catch (err) {
      throw err;
    } finally {
      setProcessingCapacityLoading(false);
    }
  };

  // Fetch and group processor capacities by municipality and marketReach
  // eslint-disable-next-line no-unused-vars
  const fetchProcessingCapacityByMunicipality = async (opts = {}) => {
    setProcessingCapacityLoading(true);
    try {
      const resp = await processorsAPI.getAll(opts);
      const processorsData = resp?.data ?? resp?.rows ?? resp ?? [];
      const normalized = Array.isArray(processorsData) ? processorsData.map((processor, index) => ({
        municipality: processor.Municipality || processor.municipality || processor.municipalityName || 'Unknown',
        marketReach: processor.Market_Reach || processor.marketReach || processor.market_reach || 'Local',
        capacity: parseFloat(processor.Processing_Capacity || processor.processingCapacity || processor.processing_capacity || 0)
      })) : [];

      const byMun = {};
      normalized.forEach(p => {
        const mun = p.municipality || 'Unknown';
        const reach = p.marketReach || 'Local';
        if (!byMun[mun]) byMun[mun] = { municipality: mun, total: 0, segments: {} };
        byMun[mun].segments[reach] = (byMun[mun].segments[reach] || 0) + (p.capacity || 0);
        byMun[mun].total += (p.capacity || 0);
      });

      const mapped = Object.values(byMun).map(m => ({
        municipality: m.municipality,
        total: m.total,
        segments: Object.entries(m.segments).map(([name, value]) => ({ name, value }))
      })).sort((a, b) => b.total - a.total).slice(0, 10);

      return mapped;
    } catch (err) {
      throw err;
    } finally {
      setProcessingCapacityLoading(false);
    }
  };

  return (
    <div className="min-h-screen py-8 bg-gradient-to-br from-gray-50 via-amber-50/30 to-green-50/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Loading State with Skeleton */}
        {loading && (
          <div className="space-y-6">
            <div className="skeleton h-24 rounded-lg"></div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="skeleton h-32 rounded-lg"></div>
              ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="skeleton h-96 rounded-lg"></div>
              ))}
            </div>
          </div>
        )}
        
        {/* Error State */}
        {error && (
          <div className="mb-6 p-4 rounded-lg bg-red-50 border-l-4 border-red-500 text-red-700 animate-slide-in-right shadow-md">
            <div className="flex items-center gap-3">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div>
                <div className="font-semibold">Failed to load analytics</div>
                <div className="text-sm">{error}</div>
              </div>
            </div>
          </div>
        )}
        
        {/* Header with Animation */}
        {!loading && (
          <div className="mb-8 animate-fade-in-up">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h1 className="mb-3 text-4xl font-bold bg-gradient-to-r from-amber-900 via-amber-700 to-green-800 bg-clip-text text-transparent">
                  Analytics Dashboard
                </h1>
                <p className="text-gray-600 max-w-2xl leading-relaxed">
                  Real-time data analysis and insights into the Bohol cacao industry, 
                  including farmer demographics, processor distribution, and production trends.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="px-4 py-2 rounded-full bg-green-100 border border-green-300 shadow-sm animate-pulse">
                  <span className="flex items-center gap-2 text-sm font-medium text-green-800">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    Live Data
                  </span>
                </div>
                <button
                  className="px-4 py-2 rounded-full bg-blue-100 border border-blue-300 text-blue-800 hover:bg-blue-200 transition-all transform hover:scale-105 shadow-sm flex items-center gap-2 text-sm font-medium"
                  onClick={async () => {
                    setLoading(true);
                    try {
                      await fetchProcessingCapacity();
                      // Refresh all data
                      const [farmersResponse, processorsResponse] = await Promise.all([
                        farmersAPI.getAll(),
                        processorsAPI.getAll()
                      ]);
                      
                      const farmersData = farmersResponse?.data ?? farmersResponse?.rows ?? farmersResponse ?? [];
                      const processorsData = processorsResponse?.data ?? processorsResponse?.rows ?? processorsResponse ?? [];
                      
                      const normalizedFarmers = mapFarmersData(farmersData);
                      const normalizedProcessors = mapProcessorsData(processorsData);
                      
                      setFarmers(normalizedFarmers);
                      setProcessors(normalizedProcessors);
                      
                      const totalHectares = normalizedFarmers.reduce((sum, f) => sum + f.landSize, 0);
                      const totalProductionKg = normalizedFarmers.reduce((sum, f) => sum + f.productionCapacity, 0);
                      
                      setStats({
                        registeredFarmers: normalizedFarmers.length,
                        activeProcessors: normalizedProcessors.length,
                        totalHectares: Math.round(totalHectares),
                        totalProductionKg: Math.round(totalProductionKg)
                      });
                    } catch (err) {
                      // refresh failed silently
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabled={processingCapacityLoading || loading}
                >
                  <svg className={`w-4 h-4 ${(processingCapacityLoading || loading) ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  {(processingCapacityLoading || loading) ? 'Refreshing...' : 'Refresh Data'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Filters with Enhanced Design */}
        {!loading && (
          <div className="bg-white p-6 rounded-xl shadow-lg mb-8 border border-gray-200 card-hover animate-scale-in">
            <div className="flex items-center gap-3 mb-4">
              <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
              </svg>
              <h3 className="text-lg font-bold text-gray-800">Analytics View</h3>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <select 
                value={selectedView} 
                onChange={(e) => setSelectedView(e.target.value)}
                className="flex-1 max-w-md rounded-lg border-2 border-gray-300 bg-white px-4 py-3 text-sm font-medium focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition-all"
              >
                <option value="all">📊 All Analytics</option>
                <option value="gender">👥 Farmer Gender Distribution</option>
                <option value="age">📅 Farmer Age Distribution</option>
                <option value="ownership">🏡 Land Ownership Distribution</option>
                <option value="municipality">📍 Municipality Distribution</option>
                <option value="businessType">🏢 Processor Business Types</option>
                <option value="marketReach">🌍 Market Reach Analysis</option>
                <option value="production">🌱 Top 10 Farmer Production</option>
                <option value="capacity">⚙️ Processor Capacity Analysis</option>
              </select>
              {selectedView !== 'all' && (
                <button
                  onClick={() => setSelectedView('all')}
                  className="px-6 py-3 text-sm font-medium rounded-lg border-2 border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 hover:from-amber-100 hover:to-orange-100 text-amber-900 transition-all transform hover:scale-105 shadow-sm"
                >
                  Show All
                </button>
              )}
            </div>
            <div className="mt-4 flex items-center gap-2">
              <div className="flex-1 h-1 bg-gradient-to-r from-amber-200 via-orange-200 to-green-200 rounded-full"></div>
              <p className="text-xs font-medium text-gray-500">
                {selectedView === 'all' 
                  ? `Displaying all ${filteredFarmers.length + filteredProcessors.length} records` 
                  : 'Filtered view active'}
              </p>
            </div>
          </div>
        )}

        {/* Enhanced Summary Stats with Animations */}
        {!loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card className="stat-card card-hover border-0 shadow-lg bg-gradient-to-br from-amber-50 to-orange-50 animate-fade-in-up" style={{ animationDelay: '0.1s', opacity: 0 }}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between mb-2">
                  <CardDescription className="text-amber-900 font-semibold">Total Farmers</CardDescription>
                  <div className="p-2 bg-amber-200 rounded-lg">
                    <svg className="w-6 h-6 text-amber-900" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                    </svg>
                  </div>
                </div>
                <CardTitle className="text-4xl font-bold bg-gradient-to-r from-amber-900 to-orange-700 bg-clip-text text-transparent">
                  {animatedFarmersCount.toLocaleString()}
                </CardTitle>
                <div className="mt-2 text-xs text-amber-700 font-medium">Registered in system</div>
              </CardHeader>
            </Card>
            
            <Card className="stat-card card-hover border-0 shadow-lg bg-gradient-to-br from-green-50 to-emerald-50 animate-fade-in-up" style={{ animationDelay: '0.2s', opacity: 0 }}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between mb-2">
                  <CardDescription className="text-green-900 font-semibold">Total Processors</CardDescription>
                  <div className="p-2 bg-green-200 rounded-lg">
                    <svg className="w-6 h-6 text-green-900" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V4a2 2 0 00-2-2H6zm1 2a1 1 0 000 2h6a1 1 0 100-2H7zm6 7a1 1 0 011 1v3a1 1 0 11-2 0v-3a1 1 0 011-1zm-3 3a1 1 0 100 2h.01a1 1 0 100-2H10zm-4 1a1 1 0 011-1h.01a1 1 0 110 2H7a1 1 0 01-1-1zm1-4a1 1 0 100 2h.01a1 1 0 100-2H7zm2 1a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1zm4-4a1 1 0 100 2h.01a1 1 0 100-2H13zM9 9a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1zM7 8a1 1 0 000 2h.01a1 1 0 000-2H7z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
                <CardTitle className="text-4xl font-bold bg-gradient-to-r from-green-900 to-emerald-700 bg-clip-text text-transparent">
                  {animatedProcessorsCount.toLocaleString()}
                </CardTitle>
                <div className="mt-2 text-xs text-green-700 font-medium">Active businesses</div>
              </CardHeader>
            </Card>
            
            <Card className="stat-card card-hover border-0 shadow-lg bg-gradient-to-br from-blue-50 to-cyan-50 animate-fade-in-up" style={{ animationDelay: '0.3s', opacity: 0 }}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between mb-2">
                  <CardDescription className="text-blue-900 font-semibold">Total Land Area</CardDescription>
                  <div className="p-2 bg-blue-200 rounded-lg">
                    <svg className="w-6 h-6 text-blue-900" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
                <CardTitle className="text-4xl font-bold bg-gradient-to-r from-blue-900 to-cyan-700 bg-clip-text text-transparent">
                  {animatedHectares.toLocaleString()}
                </CardTitle>
                <div className="mt-2 text-xs text-blue-700 font-medium">Hectares under cultivation</div>
              </CardHeader>
            </Card>
            
            <Card className="stat-card card-hover border-0 shadow-lg bg-gradient-to-br from-purple-50 to-pink-50 animate-fade-in-up" style={{ animationDelay: '0.4s', opacity: 0 }}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between mb-2">
                  <CardDescription className="text-purple-900 font-semibold">Avg. Production</CardDescription>
                  <div className="p-2 bg-purple-200 rounded-lg">
                    <svg className="w-6 h-6 text-purple-900" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                    </svg>
                  </div>
                </div>
                <CardTitle className="text-4xl font-bold bg-gradient-to-r from-purple-900 to-pink-700 bg-clip-text text-transparent">
                  {(() => {
                    const totalProd = filteredFarmers.reduce((s, f) => s + (f.productionCapacity || 0), 0);
                    const denom = filteredFarmers.length || 1;
                    const avg = denom ? Math.round(totalProd / denom) : 0;
                    return avg.toLocaleString();
                  })()}
                </CardTitle>
                <div className="mt-2 text-xs text-purple-700 font-medium">Kilograms per farmer</div>
              </CardHeader>
            </Card>
          </div>
        )}

        {/* Charts Grid with Enhanced Design */}
        {!loading && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Farmer Gender Distribution */}
            {(selectedView === 'all' || selectedView === 'gender') && (
              <Card className="border-0 shadow-xl card-hover bg-white chart-container" style={{ animationDelay: '0.5s' }}>
                <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-100">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-200 rounded-lg">
                      <svg className="w-5 h-5 text-amber-900" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                      </svg>
                    </div>
                    <div>
                      <CardTitle className="text-xl font-bold text-amber-900">Gender Distribution</CardTitle>
                      <CardDescription className="text-sm">Distribution of farmers by gender</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <SimpleStackedBar segments={genderData} />
                  <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-100">
                    <div className="text-sm font-semibold text-amber-900 mb-2">Quick Stats</div>
                    <div className="text-xs text-gray-600">
                      <div>Male: {genderData[0]?.value || 0} ({genderData[0]?.percentage || 0}%)</div>
                      <div>Female: {genderData[1]?.value || 0} ({genderData[1]?.percentage || 0}%)</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Age Group Distribution */}
            {(selectedView === 'all' || selectedView === 'age') && (
              <Card className="border-0 shadow-xl card-hover bg-white chart-container" style={{ animationDelay: '0.6s' }}>
                <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-100">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-200 rounded-lg">
                      <svg className="w-5 h-5 text-green-900" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <CardTitle className="text-xl font-bold text-green-900">Age Distribution</CardTitle>
                      <CardDescription className="text-sm">Farmers by age group</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <SimplePieChart data={ageGroupData} title="Age distribution" />
                  <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-100">
                    <div className="text-sm font-semibold text-green-900 mb-2">Age Insights</div>
                    <div className="text-xs text-gray-600">
                      Total Farmers: {ageGroupData.reduce((s, g) => s + g.value, 0)}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Land Ownership */}
            {(selectedView === 'all' || selectedView === 'ownership') && (
              <Card className="border-0 shadow-xl card-hover bg-white chart-container" style={{ animationDelay: '0.7s' }}>
                <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-100">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-200 rounded-lg">
                      <svg className="w-5 h-5 text-amber-900" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <CardTitle className="text-xl font-bold text-amber-900">Land Ownership</CardTitle>
                      <CardDescription className="text-sm">How farmers own their land</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  {(() => {
                    const lineData = (landOwnershipData || []).map(d => ({ name: d.name, value: d.count, color: d.color }));
                    return <SimpleLineChart data={lineData} dataKey="value" />;
                  })()}
                </CardContent>
              </Card>
            )}

            {/* Municipality Distribution */}
            {(selectedView === 'all' || selectedView === 'municipality') && (
              <Card className="border-0 shadow-xl card-hover bg-white chart-container" style={{ animationDelay: '0.8s' }}>
                <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-100">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-200 rounded-lg">
                      <svg className="w-5 h-5 text-green-900" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <CardTitle className="text-xl font-bold text-green-900">Municipality Distribution</CardTitle>
                      <CardDescription className="text-sm">Top municipalities by activity</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  {(() => {
                    const funnelItems = (municipalityData || []).map(m => ({
                      municipality: m.municipality,
                      farmers: m.farmers || 0,
                      processors: m.processors || 0,
                      value: (m.farmers || 0) + (m.processors || 0)
                    })).sort((a, b) => b.value - a.value).slice(0, 10);
                    return <SimpleFunnelChart items={funnelItems} labelKey="municipality" valueKey="value" />;
                  })()}
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Processor Analytics with Enhanced Design */}
        {!loading && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Business Type Distribution */}
            {(selectedView === 'all' || selectedView === 'businessType') && (
              <Card className="border-0 shadow-xl card-hover bg-white chart-container" style={{ animationDelay: '0.9s' }}>
                <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-100">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-200 rounded-lg">
                      <svg className="w-5 h-5 text-amber-900" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V8a2 2 0 00-2-2h-5L9 4H4zm7 5a1 1 0 10-2 0v1H8a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <CardTitle className="text-xl font-bold text-amber-900">Business Types</CardTitle>
                      <CardDescription className="text-sm">Distribution by structure</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  {(() => {
                    const data = (businessTypeData || []).map(b => ({ name: b.name, count: b.value, color: b.color }));
                    const colors = data.map(d => d.color);
                    return <SimpleBarChart data={data} dataKey="count" color={colors} />;
                  })()}
                </CardContent>
              </Card>
            )}

            {/* Market Reach */}
            {(selectedView === 'all' || selectedView === 'marketReach') && (
              <Card className="border-0 shadow-xl card-hover bg-white chart-container" style={{ animationDelay: '1s' }}>
                <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-100">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-200 rounded-lg">
                      <svg className="w-5 h-5 text-green-900" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM4.332 8.027a6.012 6.012 0 011.912-2.706C6.512 5.73 6.974 6 7.5 6A1.5 1.5 0 019 7.5V8a2 2 0 004 0 2 2 0 011.523-1.943A5.977 5.977 0 0116 10c0 .34-.028.675-.083 1H15a2 2 0 00-2 2v2.197A5.973 5.973 0 0110 16v-2a2 2 0 00-2-2 2 2 0 01-2-2 2 2 0 00-1.668-1.973z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <CardTitle className="text-xl font-bold text-green-900">Market Reach</CardTitle>
                      <CardDescription className="text-sm">Processor market coverage</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  {(() => {
                    const palette = {
                      Local: '#92400e',
                      Regional: '#10b981',
                      National: '#0369a1',
                      International: '#06b6d4'
                    };
                    const segments = (marketReachData || []).map(m => ({
                      name: m.reach || m.name || 'Unknown',
                      value: m.count || m.value || 0,
                      color: palette[m.reach] || '#7c3aed'
                    }));
                    return <SimpleStackedBar segments={segments} />;
                  })()}
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Production and Capacity Analysis with Enhanced Design */}
        {!loading && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Top Producers */}
            {(selectedView === 'all' || selectedView === 'production') && (
              <Card className="border-0 shadow-xl card-hover bg-white chart-container" style={{ animationDelay: '1.1s' }}>
                <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-100">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-200 rounded-lg">
                      <svg className="w-5 h-5 text-amber-900" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 0l-2 2a1 1 0 101.414 1.414L8 10.414l1.293 1.293a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <CardTitle className="text-xl font-bold text-amber-900">Top Production</CardTitle>
                      <CardDescription className="text-sm">Highest producing farmers (kg/year)</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <SimpleBubbleChart data={productionData} dataKey="production" maxRadius={44} palette={["#92400e","#f59e0b","#10b981","#06b6d4","#3b82f6","#8b5cf6","#ec4899"]} />
                </CardContent>
              </Card>
            )}

            {/* Processing Capacity */}
            {(selectedView === 'all' || selectedView === 'capacity') && (
              <Card className="border-0 shadow-xl card-hover bg-white chart-container" style={{ animationDelay: '1.2s' }}>
                <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-100">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-200 rounded-lg">
                      <svg className="w-5 h-5 text-green-900" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <CardTitle className="text-xl font-bold text-green-900">Processing Capacity</CardTitle>
                      <CardDescription className="text-sm">By business (kg/month)</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  {(() => {
                    const src = processingCapacityDataState || processingCapacityData || [];
                    const barData = src.map((p, i) => ({ name: p.name, capacity: p.capacity, color: ['#059669','#92400e','#d97706','#0369a1','#7c3aed'][i % 5] }));
                    return <SimpleBarChart data={barData} dataKey="capacity" color={barData.map(b => b.color)} />;
                  })()}
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Enhanced Insights Section */}
        {!loading && (
          <div className="mt-12 bg-gradient-to-br from-amber-100 via-orange-50 to-green-100 p-8 rounded-2xl shadow-2xl border border-amber-200 animate-fade-in-up" style={{ animationDelay: '1.3s', opacity: 0 }}>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl shadow-lg">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                  <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                </svg>
              </div>
              <h2 className="text-3xl font-bold bg-gradient-to-r from-amber-900 via-orange-800 to-green-800 bg-clip-text text-transparent">
                Key Insights
              </h2>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-xl shadow-lg card-hover border-l-4 border-green-500 animate-slide-in-right" style={{ animationDelay: '1.4s', opacity: 0 }}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <svg className="w-6 h-6 text-green-700" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                    </svg>
                  </div>
                  <h4 className="text-lg font-bold text-green-800">Gender Balance</h4>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  <span className="text-2xl font-bold text-green-700">
                    {((genderData.find(d => d.name === 'Female')?.value || 0) / Math.max(filteredFarmers.length, 1) * 100).toFixed(1)}%
                  </span>{' '}
                  of farmers are women, showing <span className="font-semibold text-green-800">
                    {((genderData.find(d => d.name === 'Female')?.value || 0) / Math.max(filteredFarmers.length, 1) * 100) >= 40 ? 'excellent' : 'good'}
                  </span> gender representation in cacao farming.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-lg card-hover border-l-4 border-amber-500 animate-slide-in-right" style={{ animationDelay: '1.5s', opacity: 0 }}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <svg className="w-6 h-6 text-amber-700" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <h4 className="text-lg font-bold text-amber-800">Geographic Coverage</h4>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Operations spanning across <span className="text-2xl font-bold text-amber-700">{municipalityData.length}</span> municipalities in Bohol, 
                  with <span className="font-semibold text-amber-800">{municipalityData[0]?.municipality || 'various areas'}</span> leading in registrations.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-lg card-hover border-l-4 border-blue-500 animate-slide-in-right" style={{ animationDelay: '1.6s', opacity: 0 }}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <svg className="w-6 h-6 text-blue-700" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM4.332 8.027a6.012 6.012 0 011.912-2.706C6.512 5.73 6.974 6 7.5 6A1.5 1.5 0 019 7.5V8a2 2 0 004 0 2 2 0 011.523-1.943A5.977 5.977 0 0116 10c0 .34-.028.675-.083 1H15a2 2 0 00-2 2v2.197A5.973 5.973 0 0110 16v-2a2 2 0 00-2-2 2 2 0 01-2-2 2 2 0 00-1.668-1.973z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <h4 className="text-lg font-bold text-blue-800">Market Expansion</h4>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  <span className="text-2xl font-bold text-blue-700">
                    {filteredProcessors.filter(p => p.marketReach === 'International' || p.marketReach === 'Local & International').length}
                  </span>{' '}
                  processors have <span className="font-semibold text-blue-800">international reach</span>, demonstrating strong export potential for Bohol cacao.
                </p>
              </div>
            </div>
            
            <div className="mt-8 text-center p-4 bg-white/50 backdrop-blur-sm rounded-lg border border-amber-200">
              <div className="flex items-center justify-center gap-2 text-sm font-medium text-gray-700">
                <svg className="w-4 h-4 text-green-600 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Live data from system • {selectedView === 'all' ? 'Showing all analytics' : 'Filtered view active'}
              </div>
              <div className="mt-2 text-xs text-gray-500">
                Last updated: {new Date().toLocaleString()}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
